import * as React from "react";

import { Body1, Caption1, Checkbox, makeStyles, tokens } from "@fluentui/react-components";

import type { MatterListItem } from "@features/matter";

import { useIsWideLayout } from "@shared/ui/LayoutContext";

import AttachmentRow from "./AttachmentRow";
import type { AttachmentRowData } from "./models";

interface AttachmentListProps {
  rows: AttachmentRowData[];
  matters: MatterListItem[];
  mattersLoading?: boolean;
  disabled?: boolean;
  onToggleAll: (selected: boolean) => void;
  onToggle: (id: string, selected: boolean) => void;
  onNameChange: (id: string, name: string) => void;
  onMatterOverrideChange: (id: string, matterId: number | null) => void;
  onRetry: (id: string) => void;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    padding: `0 ${tokens.spacingHorizontalL}`,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  count: {
    color: tokens.colorNeutralForeground3,
  },
  table: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  columns: {
    display: "grid",
    gridTemplateColumns: "1.4fr 1fr 1fr",
    columnGap: tokens.spacingHorizontalM,
    paddingInline: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
  },
  fileColumnLabel: {
    paddingLeft: "40px",
  },
  rows: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  empty: {
    color: tokens.colorNeutralForeground3,
  },
});

const AttachmentList: React.FC<AttachmentListProps> = ({
  rows,
  matters,
  mattersLoading,
  disabled,
  onToggleAll,
  onToggle,
  onNameChange,
  onMatterOverrideChange,
  onRetry,
}) => {
  const styles = useStyles();
  const isWide = useIsWideLayout();

  if (rows.length === 0) {
    return (
      <div className={styles.root}>
        <Body1 className={styles.empty}>No attachments on this message.</Body1>
      </div>
    );
  }

  const selectableRows = rows.filter((row) => row.status !== "success" && row.status !== "unsupported");
  const selectedCount = selectableRows.filter((row) => row.selected).length;
  const allSelected = selectableRows.length > 0 && selectedCount === selectableRows.length;
  const someSelected = selectedCount > 0 && !allSelected;

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Checkbox
          label="Select all"
          checked={someSelected ? "mixed" : allSelected}
          disabled={disabled}
          onChange={(_, data) => onToggleAll(Boolean(data.checked))}
        />
        <Caption1 className={styles.count}>
          {selectedCount} of {selectableRows.length} selected
        </Caption1>
      </div>

      <div className={styles.table}>
        {isWide && (
          <div className={styles.columns} aria-hidden="true">
            <Caption1 className={styles.fileColumnLabel}>File</Caption1>
            <Caption1>Name in system</Caption1>
            <Caption1>Matter</Caption1>
          </div>
        )}

        <div className={styles.rows}>
          {rows.map((row) => (
            <AttachmentRow
              key={row.id}
              row={row}
              matters={matters}
              mattersLoading={mattersLoading}
              disabled={disabled}
              onToggle={onToggle}
              onNameChange={onNameChange}
              onMatterOverrideChange={onMatterOverrideChange}
              onRetry={onRetry}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AttachmentList;
